import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import StageArt from '../../features/studio/film/StageArt';
import { LOADER_MODULES, loaderTargetForPath, type LoaderModuleId } from './loaderModules';
import './loaders.css';

const WORD = 'Frameon';
const SLOW_AFTER = 15;

function useElapsed() {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return seconds;
}

function clock(seconds: number) {
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function tilt(event: PointerEvent<HTMLElement>, target: HTMLElement | null, range: number) {
  if (!target || event.pointerType === 'touch') return;
  const box = target.getBoundingClientRect();
  const x = (event.clientX - box.left) / box.width - 0.5;
  const y = (event.clientY - box.top) / box.height - 0.5;
  target.style.setProperty('--loader-rx', `${(-y * range).toFixed(2)}deg`);
  target.style.setProperty('--loader-ry', `${(x * range).toFixed(2)}deg`);
  target.style.setProperty('--loader-mx', `${event.clientX - box.left}px`);
  target.style.setProperty('--loader-my', `${event.clientY - box.top}px`);
}

function untilt(target: HTMLElement | null) {
  target?.style.setProperty('--loader-rx', '0deg');
  target?.style.setProperty('--loader-ry', '0deg');
}

export function BrandWordmark({ className = '' }: { className?: string }) {
  return (
    <b className={`boot-shell-word${className ? ` ${className}` : ''}`} aria-label="Frameon">
      {WORD.split('').map((letter, index) => (
        <span key={index} aria-hidden="true" style={{ '--i': index } as CSSProperties}>
          {letter}
        </span>
      ))}
    </b>
  );
}

export function BrandLoader({ message = 'Opening your studio…' }: { message?: string }) {
  const shell = useRef<HTMLDivElement>(null);
  const [launch, setLaunch] = useState(0);
  const seconds = useElapsed();
  return (
    <div
      ref={shell}
      className="boot-shell"
      data-boot-shell
      role="status"
      aria-live="polite"
      onPointerMove={(event) => tilt(event, shell.current, 14)}
      onPointerLeave={() => untilt(shell.current)}
    >
      <div className="boot-shell-card">
        <div
          key={launch}
          className={`boot-shell-emblem${launch ? ' is-launch' : ''}`}
          aria-hidden="true"
          onClick={() => setLaunch((value) => value + 1)}
        >
          <span className="boot-shell-aperture" />
          <span className="boot-shell-trail" />
          <div className="boot-shell-mark">
            <i className="boot-shell-dots" />
            <i className="boot-shell-fill" />
            <i className="boot-shell-sheen" />
          </div>
        </div>
        <BrandWordmark />
        <span className="boot-shell-message">{message}</span>
        <div className="boot-shell-bar" aria-hidden="true">
          <i />
        </div>
        <small className="boot-shell-meta" aria-hidden={seconds < SLOW_AFTER}>
          {seconds >= 3 ? <span className="boot-shell-clock">{clock(seconds)}</span> : null}
          {seconds >= SLOW_AFTER ? <span>Taking longer than usual. The API may still be starting.</span> : null}
        </small>
      </div>
    </div>
  );
}

export type ModuleLoaderLayout = 'screen' | 'panel' | 'compact' | 'toast';

export function ModuleLoader({
  module,
  layout = 'panel',
  title,
}: {
  module: LoaderModuleId;
  layout?: ModuleLoaderLayout;
  title?: string;
}) {
  const config = LOADER_MODULES[module];
  const stage = useRef<HTMLDivElement>(null);
  const [replay, setReplay] = useState(0);
  const [ripple, setRipple] = useState<{ x: number; y: number; id: number } | null>(null);
  const [hint, setHint] = useState(0);
  const seconds = useElapsed();
  const rich = layout === 'screen' || layout === 'panel';
  const hints = config.hints;

  useEffect(() => {
    if (!rich || hints.length < 2) return;
    const timer = window.setInterval(() => setHint((value) => (value + 1) % hints.length), 4800);
    return () => window.clearInterval(timer);
  }, [rich, hints.length]);

  const heading = title || config.title;

  return (
    <div
      className={`module-loader module-loader--${layout}`}
      data-module={module}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {layout === 'screen' ? (
        <div className="module-loader-brand" aria-hidden="true">
          <img src="/brand/frameon-mark.svg" alt="" width="18" height="22" />
          <BrandWordmark className="is-small" />
        </div>
      ) : null}
      <div
        ref={stage}
        className="module-loader-stage"
        aria-hidden="true"
        onPointerMove={rich ? (event) => tilt(event, stage.current, 10) : undefined}
        onPointerLeave={rich ? () => untilt(stage.current) : undefined}
        onClick={
          rich
            ? (event) => {
                const box = event.currentTarget.getBoundingClientRect();
                setRipple({ x: event.clientX - box.left, y: event.clientY - box.top, id: Date.now() });
                setReplay((value) => value + 1);
              }
            : undefined
        }
      >
        {rich ? <span className="module-loader-light" /> : null}
        <div className="module-loader-art">
          <StageArt key={replay} kind={config.art} />
        </div>
        {ripple ? (
          <span key={ripple.id} className="module-loader-ripple" style={{ left: ripple.x, top: ripple.y }} />
        ) : null}
      </div>
      <div className="module-loader-copy">
        <p className="module-loader-eyebrow">{config.eyebrow}</p>
        <h2 className="module-loader-title">{heading}</h2>
        {rich && hints.length ? (
          <p key={hint} className="module-loader-hint" aria-hidden="true">
            {hints[hint]}
          </p>
        ) : null}
        <div className="module-loader-bar" aria-hidden="true">
          <i />
        </div>
        {rich ? (
          <p className="module-loader-meta" aria-hidden={seconds < SLOW_AFTER}>
            <span className="module-loader-clock">{seconds >= 2 ? clock(seconds) : ' '}</span>
            {seconds >= SLOW_AFTER ? <span>Taking longer than usual.</span> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function RouteFallback() {
  const target = loaderTargetForPath(typeof window === 'undefined' ? '/' : window.location.pathname);
  return target.kind === 'brand' ? <BrandLoader message={target.message} /> : <ModuleLoader module={target.module} layout="screen" />;
}
